
import { jsPDF } from 'jspdf';
import { BusinessIdea, ComparisonAnalysis } from '@/src/types';
import { getSafeKey } from '@/src/utils/formatters';

export interface PDFBranding {
  logo?: string;
  name?: string;
}

const loadImage = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (url.startsWith('data:')) {
      resolve(url);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } else {
        reject(new Error('Failed to get canvas context'));
      }
    };
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
};

export const generateBusinessSnapshot = async (idea: BusinessIdea, branding?: PDFBranding) => {
  const doc = new jsPDF();
  const primaryColor = '#322D29'; // #322D29 eNvesta Primary
  const secondaryColor = '#322D29'; // #322D29 eNvesta Secondary
  const margin = 20;
  let cursorY = 20;

  let logoData: string | null = null;
  if (branding?.logo) {
    try {
      logoData = await loadImage(branding.logo);
    } catch (e) {
      console.error('Failed to load branding logo:', e);
    }
  }

  // Generate a single unique ID for this specific document instance
  const dossierId = getSafeKey('dossier').toUpperCase();

  // --- Helper Functions ---
  const checkPageOverflow = (neededHeight: number) => {
    if (cursorY + neededHeight > 270) {
      doc.addPage();
      cursorY = 20;
      drawPageHeader();
    }
  };

  const drawPageHeader = () => {
    doc.setFillColor(secondaryColor);
    doc.rect(0, 0, 210, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');

    if (branding?.name) {
      doc.setFontSize(10);
      if (logoData) {
        try {
          doc.addImage(logoData, 'PNG', margin, 5, 15, 15);
          doc.text(branding.name.toUpperCase(), margin + 20, 15);
        } catch (e) {
          doc.text(branding.name.toUpperCase(), margin, 15);
        }
      } else {
        doc.text(branding.name.toUpperCase(), margin, 15);
      }
    } else {
      doc.setFontSize(10);
      doc.text('eNvesta AI Intelligence Dossier', margin, 15);
    }

    doc.text(`#${dossierId}`, 190, 15, { align: 'right' });
    cursorY = 40;
  };

  const drawSectionHeader = (title: string, color = secondaryColor) => {
    checkPageOverflow(30);
    doc.setFillColor(248, 250, 252); // slate-50
    doc.rect(0, cursorY - 5, 210, 15, 'F');
    doc.setTextColor(color);
    doc.setFontSize(11); // Professional 11px
    doc.setFont('helvetica', 'bold');
    doc.text(title.toUpperCase(), margin, cursorY + 5);
    cursorY += 20;
  };

  const addParagraph = (text: string, fontSize = 9, fontStyle = 'normal', color = [50, 45, 41]) => {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', fontStyle);
    doc.setTextColor(color[0], color[1], color[2]);
    
    const lines: string[] = doc.splitTextToSize(text, 170);
    const lineHeight = fontSize * 0.5;
    
    let currentLineIndex = 0;
    while (currentLineIndex < lines.length) {
      const remainingPageHeight = 270 - cursorY;
      const linesThatFit = Math.max(1, Math.floor(remainingPageHeight / lineHeight));
      
      const chunk = lines.slice(currentLineIndex, currentLineIndex + linesThatFit);
      doc.text(chunk, margin, cursorY);
      
      currentLineIndex += chunk.length;
      cursorY += (chunk.length * lineHeight) + 5;

      if (currentLineIndex < lines.length) {
        doc.addPage();
        cursorY = 20;
        drawPageHeader();
      }
    }
    cursorY += 5; // Add some spacing after the paragraph
  };

  // --- Page 1: Executive Summary ---
  doc.setFillColor(secondaryColor);
  doc.rect(0, 0, 210, 297, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(40);
  doc.setFont('helvetica', 'bold');
  doc.text('VENTURE', margin, 100);
  doc.text('ARCHITECTURE', margin, 115);
  doc.text('DOSSIER', margin, 130);
  
  doc.setFillColor(primaryColor);
  doc.rect(margin, 140, 40, 2, 'F');
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.text(idea.title.toUpperCase(), margin, 155);
  doc.text(idea.category.toUpperCase(), margin, 165);
  
  doc.setFontSize(10);
  doc.text(`GENERATED: ${new Date().toLocaleDateString()}`, margin, 250);
  doc.text(`CONFIDENTIAL INTEL: #${dossierId}`, margin, 255);
  doc.text('ENVESTA AI CORE v2.5', margin, 260);

  // --- Start Content ---
  doc.addPage();
  drawPageHeader();
  drawSectionHeader('Executive Summary & Vision');
  
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(secondaryColor);
  doc.text(idea.title, margin, cursorY);
  cursorY += 15;

  addParagraph(idea.description, 12, 'italic', [15, 23, 42]);
  
  if (idea.culturalSignificance) {
    drawSectionHeader('Cultural Significance');
    addParagraph(idea.culturalSignificance);
  }

  // --- Market Analysis ---
  if (idea.marketAnalysis || idea.competition) {
    drawSectionHeader('Market Intelligence & Competition');
    
    if (idea.marketAnalysis) {
      addParagraph(idea.marketAnalysis, 11);
    }

    if (idea.competition) {
      checkPageOverflow(40);
      cursorY += 10;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(secondaryColor);
      doc.text('COMPETITIVE LANDSCAPE', margin, cursorY);
      cursorY += 10;

      const compStats = [
        ['Nearest Competitor', idea.competition.nearestCompetitor],
        ['Distance to Node', idea.competition.distance],
        ['Competitor Capacity', idea.competition.competitorCapacity],
        ['Strategic Advantage', idea.competition.ourAdvantage]
      ];

      compStats.forEach(([label, value]) => {
        const valLines = doc.splitTextToSize(value, 100);
        const neededHeight = Math.max(15, (valLines.length * 5) + 5);
        checkPageOverflow(neededHeight);
        
        doc.setFillColor(248, 250, 252);
        doc.rect(margin, cursorY, 170, neededHeight - 3, 'F');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(secondaryColor);
        doc.text(label, margin + 5, cursorY + 8);
        doc.setFont('helvetica', 'normal');
        doc.text(valLines, 180, cursorY + 8, { align: 'right' });
        cursorY += neededHeight;
      });
    }
  }

  // --- LaunchMath Path ---
  if (idea.fullPath) {
    drawSectionHeader('LaunchMath: Full Deployment Path');
    
    checkPageOverflow(20);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(secondaryColor);
    doc.text('INSTITUTIONAL SCALE PROTOCOL', margin, cursorY);
    cursorY += 10;

    const stats = [
      ['Startup Capital', idea.fullPath.startupCost],
      ['12-Month Runway', idea.fullPath.runway12Mo],
      ['Break-Even Timeline', idea.fullPath.breakEvenTimeline],
      ['Potential ROI', idea.potentialROI || '18-24%']
    ];

    stats.forEach(([label, value]) => {
      checkPageOverflow(15);
      doc.setFillColor(248, 250, 252);
      doc.rect(margin, cursorY, 170, 12, 'F');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(secondaryColor);
      doc.text(label, margin + 5, cursorY + 8);
      doc.setFont('helvetica', 'normal');
      doc.text(value, 180, cursorY + 8, { align: 'right' });
      cursorY += 15;
    });

    cursorY += 10;
    checkPageOverflow(20);
    doc.setFont('helvetica', 'bold');
    doc.text('STRATEGIC ENTRY VECTOR', margin, cursorY);
    cursorY += 8;
    addParagraph(idea.fullPath.entryStrategy || "Standard market entry protocol.");
  }

  // --- Lean Entry ---
  if (idea.leanPath) {
    drawSectionHeader('LaunchMath: Lean Startup Path');
    
    checkPageOverflow(20);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor);
    doc.text('VALIDATION-FIRST ENTRY PROTOCOL', margin, cursorY);
    cursorY += 10;

    const stats = [
      ['Minimum Viable Capital', idea.leanPath.startupCost],
      ['Validation Runway', idea.leanPath.runway12Mo],
      ['Agile Break-Even', idea.leanPath.breakEvenTimeline]
    ];

    stats.forEach(([label, value]) => {
      checkPageOverflow(15);
      doc.setFillColor(255, 247, 237);
      doc.rect(margin, cursorY, 170, 12, 'F');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor);
      doc.text(label, margin + 5, cursorY + 8);
      doc.setFont('helvetica', 'normal');
      doc.text(value, 180, cursorY + 8, { align: 'right' });
      cursorY += 15;
    });

    cursorY += 10;
    checkPageOverflow(20);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(secondaryColor);
    doc.text('LEAN VALIDATION STRATEGY', margin, cursorY);
    cursorY += 8;
    addParagraph(idea.leanPath.entryStrategy || "Lean market validation protocol.");
  }

  // --- RiskGuard ---
  if (idea.riskGuard && idea.riskGuard.score !== undefined) {
    drawSectionHeader('RiskGuard Resilience Protocol');
    
    checkPageOverflow(40);
    doc.setFontSize(40);
    doc.setFont('helvetica', 'bold');
    const score = idea.riskGuard.score;
    const scoreColor = score > 7 ? [225, 29, 72] : [16, 185, 129];
    doc.setTextColor(scoreColor[0], scoreColor[1], scoreColor[2]);
    doc.text(`${score}`, margin, cursorY + 20);
    
    doc.setFontSize(12);
    doc.setTextColor(secondaryColor);
    doc.text('/ 10 RISK SCORE', margin + 25, cursorY + 20);
    cursorY += 40;

    const risks = [
      ['Financial Exposure', idea.riskGuard.financial],
      ['Market Volatility', idea.riskGuard.market],
      ['Regulatory Protocol', idea.riskGuard.regulatory]
    ];

    risks.forEach(([label, text]) => {
      if (text) {
        checkPageOverflow(20);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(label.toUpperCase(), margin, cursorY);
        cursorY += 7;
        addParagraph(text);
        cursorY += 5;
      }
    });
  }

  // --- Roadmap ---
  if (idea.strategicRoadmap && idea.strategicRoadmap.length > 0) {
    drawSectionHeader('Strategic Operational Roadmap');
    
    idea.strategicRoadmap.forEach((step, i) => {
      checkPageOverflow(30);
      doc.setFillColor(secondaryColor);
      doc.rect(margin, cursorY, 10, 10, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.text(`${i + 1}`, margin + 4, cursorY + 7);
      
      doc.setTextColor(secondaryColor);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`PHASE ${i + 1}`, margin + 15, cursorY + 7);
      cursorY += 12;
      addParagraph(step);
    });
  }

  // --- Site Recon & Entities ---
  if (idea.siteRecommendations && idea.siteRecommendations.length > 0) {
    drawSectionHeader('Tactical Site Reconnaissance');
    
    idea.siteRecommendations.forEach((site, i) => {
      checkPageOverflow(25);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(`${i + 1}. ${site.name}`, margin, cursorY);
      cursorY += 6;
      addParagraph(`Rationale: ${site.reason}`, 9, 'italic');
    });
  }

  if (idea.spottedEntities && idea.spottedEntities.length > 0) {
    drawSectionHeader('Verified Regional Entities');
    idea.spottedEntities.slice(0, 8).forEach((entity) => {
      checkPageOverflow(20);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(entity.name, margin, cursorY);
      cursorY += 5;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(entity.address || 'Verified Corridor', margin, cursorY);
      cursorY += 10;
    });
  }

  // --- Temporal Analysis ---
  if (idea.temporalTrend || (idea.historicalTrendData && idea.historicalTrendData.length > 0)) {
    drawSectionHeader('Temporal Analysis & Trend Prediction');
    
    if (idea.temporalTrend) {
      addParagraph(idea.temporalTrend);
    }

    if (idea.historicalTrendData && idea.historicalTrendData.length > 0) {
      checkPageOverflow(30);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('HISTORICAL INTENSITY AUDIT', margin, cursorY);
      cursorY += 10;

      idea.historicalTrendData.forEach((point) => {
        checkPageOverflow(10);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(`${point.period}: ${point.intensity}% Intensity`, margin, cursorY);
        cursorY += 7;
      });
    }
  }

  // --- LaunchMath Analysis ---
  if (idea.launchMathDeepDive) {
    const math = idea.launchMathDeepDive;
    drawSectionHeader('LaunchMath Detailed Operations');

    const fields = [
      { label: 'Startup Capital Allocation', text: math.startupCapital },
      { label: 'Monthly Operational Expenditure (OpEx)', text: math.monthlyOpEx },
      { label: 'Revenue Streams & Monetization Vectors', text: math.revenueStreams },
      { label: 'Break-Even Threshold Analysis', text: math.breakEvenAnalysis }
    ];

    fields.forEach((field) => {
      if (field.text) {
        checkPageOverflow(30);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(secondaryColor);
        doc.text(field.label.toUpperCase(), margin, cursorY);
        cursorY += 7;
        addParagraph(field.text, 9.5, 'normal', [71, 85, 105]);
        cursorY += 3;
      }
    });
  }

  // --- SiteRecon Deep Dive ---
  if (idea.siteReconDeepDive) {
    const recon = idea.siteReconDeepDive;
    drawSectionHeader('Site Reconnaissance Micro-Targeting');

    const fields = [
      { label: 'Micro-Location Market Fit', text: recon.microLocationFit },
      { label: 'Accessibility & Logistic Corridors', text: recon.accessibilityLogistics },
      { label: 'Regional Demographic Alignment', text: recon.demographicAlignment },
      { label: 'Visual & Environment Landscape Map', text: recon.visualLandscape }
    ];

    fields.forEach((field) => {
      if (field.text) {
        checkPageOverflow(30);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(secondaryColor);
        doc.text(field.label.toUpperCase(), margin, cursorY);
        cursorY += 7;
        addParagraph(field.text, 9.5, 'normal', [71, 85, 105]);
        cursorY += 3;
      }
    });
  }

  // --- RiskGuard Deep Dive ---
  if (idea.riskGuardDeepDive) {
    const risk = idea.riskGuardDeepDive;
    drawSectionHeader('RiskGuard Deep Assessment');

    const fields = [
      { label: 'Regulatory & Zoning Protocols', text: risk.regulatoryZoning },
      { label: 'Market Saturation & Growth Thresholds', text: risk.marketSaturation },
      { label: 'Operational Supply Chain Integrity', text: risk.operationalSupplyChain }
    ];

    fields.forEach((field) => {
      if (field.text) {
        checkPageOverflow(30);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(secondaryColor);
        doc.text(field.label.toUpperCase(), margin, cursorY);
        cursorY += 7;
        addParagraph(field.text, 9.5, 'normal', [71, 85, 105]);
        cursorY += 3;
      }
    });

    if (risk.financialStressTest) {
      checkPageOverflow(35);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(secondaryColor);
      doc.text('FINANCIAL STRESS TESTING', margin, cursorY);
      cursorY += 10;

      const scenarios = [
        { label: 'Best Case Scenario', text: risk.financialStressTest.bestCase },
        { label: 'Expected Base Case Scenario', text: risk.financialStressTest.expectedCase },
        { label: 'Worst Case Contingency Case', text: risk.financialStressTest.worstCase }
      ];

      scenarios.forEach((scen) => {
        if (scen.text) {
          checkPageOverflow(25);
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(primaryColor);
          doc.text(scen.label.toUpperCase(), margin, cursorY);
          cursorY += 6;
          addParagraph(scen.text, 9, 'normal', [71, 85, 105]);
          cursorY += 2;
        }
      });
    }

    if (risk.mitigationRoadmap) {
      checkPageOverflow(30);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(secondaryColor);
      doc.text('RISK MITIGATION ROADMAP', margin, cursorY);
      cursorY += 7;
      addParagraph(risk.mitigationRoadmap, 9.5, 'normal', [71, 85, 105]);
    }
  }

  // --- 18-Month Scaling Strategy ---
  if (idea.scalingStrategyDeepDive) {
    const scaling = idea.scalingStrategyDeepDive;
    drawSectionHeader('18-Month Scaling Strategy');

    if (scaling.first90Days && scaling.first90Days.length > 0) {
      checkPageOverflow(20);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(secondaryColor);
      doc.text('THE FIRST 90 DAYS', margin, cursorY);
      cursorY += 10;

      scaling.first90Days.forEach((milestone) => {
        checkPageOverflow(30);
        doc.setFillColor(248, 250, 252);
        doc.rect(margin, cursorY, 170, 7, 'F');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(primaryColor);
        doc.text(milestone.week.toUpperCase(), margin + 5, cursorY + 5);
        cursorY += 10;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(secondaryColor);
        doc.text(milestone.milestone, margin, cursorY);
        cursorY += 6;

        addParagraph(milestone.howTo, 9, 'normal', [71, 85, 105]);
        cursorY += 2;
      });
    }

    if (scaling.quarterlyScaling && scaling.quarterlyScaling.length > 0) {
      checkPageOverflow(20);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(secondaryColor);
      doc.text('QUARTERLY SCALING ROADMAP', margin, cursorY);
      cursorY += 10;

      scaling.quarterlyScaling.forEach((goal) => {
        checkPageOverflow(30);
        doc.setFillColor(241, 245, 249);
        doc.rect(margin, cursorY, 170, 7, 'F');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(primaryColor);
        doc.text(goal.period.toUpperCase(), margin + 5, cursorY + 5);
        cursorY += 10;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(secondaryColor);
        doc.text(goal.goal, margin, cursorY);
        cursorY += 6;

        addParagraph(goal.howTo, 9, 'normal', [71, 85, 105]);
        cursorY += 2;
      });
    }

    if (scaling.trendAnalysis && scaling.trendAnalysis.length > 0) {
      checkPageOverflow(20);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(secondaryColor);
      doc.text('TREND ANALYSIS & MARKET SHIFTS', margin, cursorY);
      cursorY += 10;

      scaling.trendAnalysis.forEach((trend) => {
        checkPageOverflow(30);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(secondaryColor);
        doc.text(trend.trend, margin, cursorY);
        cursorY += 6;

        addParagraph(trend.description, 9, 'normal', [71, 85, 105]);
        
        checkPageOverflow(15);
        doc.setFillColor(254, 243, 199);
        doc.rect(margin, cursorY, 170, 8, 'F');
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(primaryColor);
        doc.text('STRATEGIC IMPACT:', margin + 5, cursorY + 6);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(secondaryColor);
        doc.text(trend.impact, margin + 40, cursorY + 6);
        cursorY += 13;
      });
    }

    if (scaling.pivotPoints && scaling.pivotPoints.length > 0) {
      checkPageOverflow(20);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(secondaryColor);
      doc.text('STRATEGIC CONTINGENCY & PIVOT POINTS', margin, cursorY);
      cursorY += 10;

      scaling.pivotPoints.forEach((pivot) => {
        checkPageOverflow(30);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(secondaryColor);
        doc.text(`Scenario: ${pivot.scenario}`, margin, cursorY);
        cursorY += 6;

        addParagraph(`Pivot Strategy: ${pivot.strategy}`, 9, 'normal', [71, 85, 105]);
        cursorY += 2;
      });
    }
  }

  // --- Intelligence Hub ---
  if (idea.intelligenceHub) {
    const intel = idea.intelligenceHub;
    drawSectionHeader('Intelligence Hub Deep Analysis');

    const fields = [
      { label: 'Competitor Deep Dive', text: intel.competitorDeepDive },
      { label: 'Differentiation Engine', text: intel.differentiationEngine },
      { label: 'Market Share Estimation', text: intel.marketShareEstimation },
      { label: 'Seasonality Forecasting', text: intel.seasonalityForecasting },
      { label: 'Gentrification Trajectory', text: intel.gentrificationTrajectory },
      { label: 'Economic Cycle Resilience', text: intel.economicCycleResilience },
      { label: 'Persona Generator', text: intel.personaGenerator },
      { label: 'Foot Traffic Patterns', text: intel.footTrafficPatterns },
      { label: 'Sentiment Analysis', text: intel.sentimentAnalysis }
    ];

    fields.forEach((field) => {
      if (field.text) {
        checkPageOverflow(30);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(secondaryColor);
        doc.text(field.label.toUpperCase(), margin, cursorY);
        cursorY += 7;
        addParagraph(field.text, 9.5, 'normal', [71, 85, 105]);
        cursorY += 3;
      }
    });
  }

  // --- Operations Hub ---
  if (idea.operationsHub) {
    const ops = idea.operationsHub;
    drawSectionHeader('Operations Hub Architecture');

    const fields = [
      { label: 'Scenario Modeling', text: ops.scenarioModeling },
      { label: 'Investor Matchmaking', text: ops.investorMatchmaking },
      { label: 'Grant Scanner', text: ops.grantScanner },
      { label: 'Labor Market Analytics', text: ops.laborMarketAnalytics },
      { label: 'Supply Chain Mapping', text: ops.supplyChainMapping },
      { label: 'Regulatory Navigator', text: ops.regulatoryNavigator },
      { label: 'Performance Benchmarking', text: ops.performanceBenchmarking },
      { label: 'Expansion Scouting', text: ops.expansionScouting },
      { label: 'Exit Strategy Modeling', text: ops.exitStrategyModeling }
    ];

    fields.forEach((field) => {
      if (field.text) {
        checkPageOverflow(30);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(secondaryColor);
        doc.text(field.label.toUpperCase(), margin, cursorY);
        cursorY += 7;
        addParagraph(field.text, 9.5, 'normal', [71, 85, 105]);
        cursorY += 3;
      }
    });
  }

  // --- Collaboration Hub ---
  if (idea.collaborationHub) {
    const collab = idea.collaborationHub;
    drawSectionHeader('Collaboration Hub & Enterprise Integration');

    const fields = [
      { label: 'Stakeholder Sharing Protocol', text: collab.stakeholderSharing },
      { label: 'Version Control Logging', text: collab.versionControl },
      { label: 'Commentary & Annotations', text: collab.commentaryAnnotations },
      { label: 'Algorithmic Transparency Policy', text: collab.algorithmicTransparency },
      { label: 'Bias & Inclusivity Detection', text: collab.biasDetection },
      { label: 'Data Privacy Controls', text: collab.dataPrivacyControls },
      { label: 'CRM Dynamic Connectors', text: collab.crmConnectors },
      { label: 'ERP Institutional Integrations', text: collab.erpIntegrations },
      { label: 'Core API Gateway Access', text: collab.apiAccess },
      { label: 'Rural Market Localization', text: collab.ruralMarketOptimization },
      { label: 'Multi-Region Full Localization', text: collab.fullLocalization },
      { label: 'Offline-First Workspace Mode', text: collab.advancedOfflineMode }
    ];

    fields.forEach((field) => {
      if (field.text) {
        checkPageOverflow(30);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(secondaryColor);
        doc.text(field.label.toUpperCase(), margin, cursorY);
        cursorY += 7;
        addParagraph(field.text, 9.5, 'normal', [71, 85, 105]);
        cursorY += 3;
      }
    });
  }

  // --- Reconnaissance System Metadata ---
  drawSectionHeader('System Intelligence & Reconnaissance Metrics');

  // String and Number fields
  checkPageOverflow(40);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(secondaryColor);
  doc.text('CORE DIMENSION INDICATORS', margin, cursorY);
  cursorY += 8;

  const coreIndicators: [string, any][] = [
    ['System ID', idea.id || 'N/A'],
    ['Venture UUID', idea.uuid || 'N/A'],
    ['Recon Date', idea.date || new Date().toLocaleDateString()],
    ['Hub Origin Source', idea.hubOrigin || 'N/A'],
    ['Inherent Risk Rating', idea.riskLevel || 'N/A'],
    ['Escalated Growth Potential', idea.growthPotential || 'N/A'],
    ['Security Scrutiny Index', idea.scrutinyId || 'N/A'],
    ['Enterprise Privacy', idea.privacy || 'N/A'],
    ['Target Micro-Region', idea.locationName || 'N/A'],
    ['Computed Market Demand Score', idea.marketDemandScore !== undefined ? `${idea.marketDemandScore}/100` : 'N/A'],
    ['Computed Saturation Index', idea.saturationIndex !== undefined ? `${idea.saturationIndex}/100` : 'N/A'],
  ];

  coreIndicators.forEach(([lbl, val]) => {
    checkPageOverflow(12);
    doc.setFillColor(248, 250, 252);
    doc.rect(margin, cursorY, 170, 9, 'F');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(secondaryColor);
    doc.text(lbl, margin + 4, cursorY + 6);
    doc.setFont('helvetica', 'normal');
    doc.text(String(val), 180, cursorY + 6, { align: 'right' });
    cursorY += 11;
  });

  // Long text / AI insights (wrap text, reduce to 8px, allow page breaks)
  if (idea.densityInsight) {
    checkPageOverflow(25);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(secondaryColor);
    doc.text('DENSITY INTENSITY INSIGHT', margin, cursorY);
    cursorY += 7;
    addParagraph(idea.densityInsight, 8, 'normal', [71, 85, 105]);
  }

  if (idea.context) {
    checkPageOverflow(25);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(secondaryColor);
    doc.text('REGIONAL STRUCTURAL CONTEXT', margin, cursorY);
    cursorY += 7;
    addParagraph(idea.context, 8, 'normal', [71, 85, 105]);
  }

  // Arrays rendered as bulleted lists
  if (idea.tags && idea.tags.length > 0) {
    checkPageOverflow(25);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(secondaryColor);
    doc.text('VENTURE TAGS & METADATA', margin, cursorY);
    cursorY += 8;
    
    idea.tags.forEach(tag => {
      checkPageOverflow(10);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);
      doc.text(`- ${tag}`, margin + 5, cursorY);
      cursorY += 5;
    });
    cursorY += 3;
  }

  if (idea.sources && idea.sources.length > 0) {
    checkPageOverflow(25);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(secondaryColor);
    doc.text('GROUNDING INTEL SOURCES', margin, cursorY);
    cursorY += 8;

    idea.sources.forEach(source => {
      checkPageOverflow(12);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(secondaryColor);
      doc.text(`- ${source.title}`, margin + 5, cursorY);
      cursorY += 4;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);
      doc.text(`  URI: ${source.uri}`, margin + 5, cursorY);
      cursorY += 6;
    });
    cursorY += 3;
  }

  if (idea.comments && idea.comments.length > 0) {
    checkPageOverflow(25);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(secondaryColor);
    doc.text('COLLABORATIVE ANNOTATIONS', margin, cursorY);
    cursorY += 8;

    idea.comments.forEach(comment => {
      checkPageOverflow(15);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(secondaryColor);
      const dateStr = new Date(comment.timestamp).toLocaleDateString();
      doc.text(`- ${comment.userName} (${dateStr})`, margin + 5, cursorY);
      cursorY += 4;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);
      const wrappedCommentText = doc.splitTextToSize(comment.text, 160);
      doc.text(wrappedCommentText, margin + 9, cursorY);
      cursorY += (wrappedCommentText.length * 4) + 3;
    });
    cursorY += 3;
  }

  if (idea.snapshots && idea.snapshots.length > 0) {
    checkPageOverflow(25);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(secondaryColor);
    doc.text('VENTURE ARCHITECTURE SNAPSHOTS', margin, cursorY);
    cursorY += 8;

    idea.snapshots.forEach(snapshot => {
      checkPageOverflow(12);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(secondaryColor);
      const snapDate = new Date(snapshot.timestamp).toLocaleDateString();
      doc.text(`- ${snapshot.label || 'Venture State Snapshot'} (${snapDate})`, margin + 5, cursorY);
      cursorY += 4;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);
      doc.text(`  ID: ${snapshot.id}`, margin + 9, cursorY);
      cursorY += 6;
    });
    cursorY += 3;
  }

  // Nested Structured Entities (rendered as labeled subsections)
  if (idea.integrations) {
    checkPageOverflow(25);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(secondaryColor);
    doc.text('ENTERPRISE DATA INTEGRATION STATUS', margin, cursorY);
    cursorY += 8;

    const integrationsList = [
      { key: 'Salesforce Connect', val: idea.integrations.salesforce },
      { key: 'HubSpot Sync', val: idea.integrations.hubspot },
      { key: 'SAP Corporate ERP', val: idea.integrations.sap }
    ];

    integrationsList.forEach(item => {
      checkPageOverflow(12);
      doc.setFillColor(248, 250, 252);
      doc.rect(margin + 5, cursorY, 165, 8, 'F');
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(secondaryColor);
      doc.text(item.key, margin + 9, cursorY + 5);
      
      const statusText = item.val?.connected 
        ? `CONNECTED (Last Sync: ${item.val.lastSync ? new Date(item.val.lastSync).toLocaleDateString() : 'N/A'})` 
        : 'DISCONNECTED';
      const statusColor = item.val?.connected ? [16, 185, 129] : [100, 116, 139];
      
      doc.setTextColor(statusColor[0], statusColor[1], statusColor[2]);
      doc.text(statusText, 180, cursorY + 5, { align: 'right' });
      cursorY += 10;
    });
    cursorY += 3;
  }

  if (idea.actuals) {
    checkPageOverflow(30);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(secondaryColor);
    doc.text('VENTURE ACTUALS PERFORMANCE AUDIT', margin, cursorY);
    cursorY += 8;

    const actualSections = [
      { key: 'Financial Revenue', data: idea.actuals.revenue, suffix: ' USD' },
      { key: 'Operational Expenses', data: idea.actuals.expenses, suffix: ' USD' },
      { key: 'Customer Volume Metrics', data: idea.actuals.customers, suffix: '' }
    ];

    actualSections.forEach(sect => {
      if (sect.data && sect.data.length > 0) {
        checkPageOverflow(16 + (sect.data.length * 6));
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(secondaryColor);
        doc.text(sect.key.toUpperCase(), margin + 5, cursorY);
        cursorY += 5;

        sect.data.forEach(item => {
          doc.setFillColor(248, 250, 252);
          doc.rect(margin + 10, cursorY, 155, 6, 'F');
          
          doc.setFontSize(7.5);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(71, 85, 105);
          doc.text(item.period, margin + 14, cursorY + 4);
          
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(secondaryColor);
          doc.text(`${item.value.toLocaleString()}${sect.suffix}`, 170, cursorY + 4, { align: 'right' });
          cursorY += 7;
        });
        cursorY += 2;
      }
    });
  }

  // --- Consistent Header & Footer Application ---
  const pageCount = (doc as any).internal.getNumberOfPages();
  const timestampStr = new Date().toLocaleString();
  
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    
    // Draw/render page header bar on ALL pages
    doc.setFillColor('#322D29');
    doc.rect(0, 0, 210, 25, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    // Draw "ENVESTA AI RECONNAISSANCE REPORT" with "AI" in Orange color
    let currentX = margin;
    doc.text('ENVESTA ', currentX, 15);
    currentX += doc.getTextWidth('ENVESTA ');
    
    doc.setTextColor('#F97316'); // Orange
    doc.text('AI ', currentX, 15);
    currentX += doc.getTextWidth('AI ');
    
    doc.setTextColor(255, 255, 255);
    doc.text('RECONNAISSANCE REPORT', currentX, 15);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(timestampStr, 115, 15, { align: 'center' });
    doc.text(`PAGE ${i} OF ${pageCount}`, 190, 15, { align: 'right' });

    // Footer
    doc.setFillColor(241, 245, 249);
    doc.rect(0, 275, 210, 22, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.line(0, 275, 210, 275);
    
    // Tiny disclaimer text
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5.5);
    doc.setTextColor(100, 116, 139);
    const disclaimerText = "eNvesta AI provides market intelligence and business planning tools, not financial, investment, or legal advice. All outputs are algorithmic estimates based on aggregated data and your inputs. Consult qualified professionals before committing capital. Past performance of similar businesses does not guarantee future results.";
    const disclaimerLines = doc.splitTextToSize(disclaimerText, 170);
    doc.text(disclaimerLines, margin, 280);
    
    doc.setFontSize(7);
    doc.text(`Venture ID: #${dossierId}`, margin, 293);
    doc.text(`Page ${i} of ${pageCount}`, 190, 293, { align: 'right' });
    doc.setFont('helvetica', 'bold');
    
    if (branding?.name) {
      if (logoData) {
        try {
          doc.addImage(logoData, 'PNG', 105 - 15, 285, 8, 8);
          doc.text(branding.name, 105, 293, { align: 'center' });
        } catch (e) {
          doc.text(branding.name, 105, 293, { align: 'center' });
        }
      } else {
        doc.text(branding.name, 105, 293, { align: 'center' });
      }
    } else {
      doc.text('eNvesta AI Intelligence Engine v2.5', 105, 293, { align: 'center' });
    }
  }

  doc.save(`eNvesta_Dossier_${idea.title.replace(/\s+/g, '_')}.pdf`);
};

export const generateComparisonReport = async (ideas: BusinessIdea[], comparison?: ComparisonAnalysis, branding?: PDFBranding) => {
  const doc = new jsPDF('p', 'mm', 'a4'); // Portrait for better flow with AI text
  const primaryColor = '#322D29'; // #322D29 eNvesta Primary
  const secondaryColor = '#322D29'; // #322D29 eNvesta Secondary
  const margin = 20;
  let cursorY = 35; // Start with 35 to leave plenty of room above the header overlay

  let logoData: string | null = null;
  if (branding?.logo) {
    try {
      logoData = await loadImage(branding.logo);
    } catch (e) {
      console.error('Failed to load branding logo:', e);
    }
  }

  const checkPageOverflow = (neededHeight: number) => {
    if (cursorY + neededHeight > 265) {
      doc.addPage();
      cursorY = 35; // Start new page with space for header overlay
    }
  };

  const addWrappedText = (text: string, width: number, fontSize: number, style = 'normal') => {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', style);
    const lines = doc.splitTextToSize(text, width);
    const neededHeight = lines.length * (fontSize * 0.45);
    checkPageOverflow(neededHeight);
    doc.text(lines, margin, cursorY);
    cursorY += neededHeight;
    return lines;
  };

  // ==========================================
  // COVER PAGE (PAGE 1)
  // ==========================================
  
  // Outer elegant geometric frame / design accents
  doc.setLineWidth(0.5);
  doc.setDrawColor(50, 45, 41); // #322D29 eNvesta Primary
  doc.line(15, 15, 195, 15); // Top thin boundary accent
  doc.line(15, 282, 195, 282); // Bottom thin boundary accent
  
  // Center main content
  // Sub-header category
  doc.setTextColor(100, 116, 139); // Slate-500
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('AUTOMATED MARKET INTELLIGENCE SYSTEM', 105, 80, { align: 'center' });

  // Draw giant brand title - eNvesta AI
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  const brandPart1 = 'eNvesta ';
  const brandPart2 = 'AI';
  const w1 = doc.getTextWidth(brandPart1);
  const w2 = doc.getTextWidth(brandPart2);
  const totalW = w1 + w2;
  const startX = 105 - (totalW / 2);
  
  doc.setTextColor(50, 45, 41); // #322D29 eNvesta Primary
  doc.text(brandPart1, startX, 105);
  doc.setTextColor('#F97316'); // Orange Accent
  doc.text(brandPart2, startX + w1, 105);
  
  // Divider line
  doc.setLineWidth(1);
  doc.setDrawColor(249, 115, 22); // Orange Accent
  doc.line(75, 115, 135, 115);
  
  // Subtitle
  doc.setTextColor(50, 45, 41);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('INTEL COMPARISON & COMPREHENSIVE MATRIX', 105, 128, { align: 'center' });
  
  // Target ventures subtitle list
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9.5);
  doc.setTextColor(71, 85, 105); // Slate-600
  const ideaTitles = ideas.map(i => i.title.toUpperCase()).join('  |  ');
  const ideaLines = doc.splitTextToSize(ideaTitles, 160);
  doc.text(ideaLines, 105, 140, { align: 'center' });

  // Middle Graphic Accent: Clean clinical box
  doc.setFillColor(248, 250, 252); // Soft light slate bg
  doc.rect(25, 160, 160, 42, 'F');
  doc.setDrawColor(226, 232, 240); // slate-200
  doc.setLineWidth(0.3);
  doc.rect(25, 160, 160, 42, 'D');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139); // Slate-500
  doc.text('EXECUTIVE SYSTEM RECONNAISSANCE SUMMARY', 105, 172, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(51, 65, 85); // Slate-700
  const summaryBoxText = comparison 
    ? comparison.summary.slice(0, 150) + "..."
    : `Comparing ${ideas.length} subject ventures across key market indicators, demand metrics, capital requirements, and projected ROI. Powered by eNvesta advanced AI engine.`;
  const summaryBoxLines = doc.splitTextToSize(summaryBoxText, 140);
  doc.text(summaryBoxLines, 105, 182, { align: 'center' });

  // Bottom Metadata Drawer
  doc.setLineWidth(0.5);
  doc.setDrawColor(226, 232, 240);
  doc.line(30, 220, 180, 220);

  // Stats/Metadata grid on cover page
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  
  doc.text('REPORT CLASSIFICATION:', 35, 232);
  doc.text('DATE GENERATED:', 35, 239);
  doc.text('TOTAL VENTURES COMPARED:', 35, 246);
  doc.text('SYSTEM ID:', 35, 253);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(51, 65, 85);
  doc.text('CONFIDENTIAL / EXPERT SYSTEM ANALYSIS', 95, 232);
  doc.text(new Date().toLocaleString(), 95, 239);
  doc.text(`${ideas.length} VENTURES`, 95, 246);
  doc.text(`ENV-${new Date().getTime().toString(36).toUpperCase()}-CMP`, 95, 253);

  // Add the page to start the standard report on Page 2!
  doc.addPage();

  // 1. AI Strategic Briefing (If available)
  if (comparison) {
    doc.setTextColor(primaryColor);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('AI STRATEGIC BRIEFING', margin, cursorY);
    cursorY += 8;

    doc.setTextColor(secondaryColor);
    doc.setFontSize(14);
    doc.text('Executive Summary', margin, cursorY);
    cursorY += 10;

    addWrappedText(comparison.summary, 170, 10);
    cursorY += 10;

    // Gaps & Risks
    checkPageOverflow(30);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('CRITICAL GAPS', margin, cursorY);
    doc.text('COMPARATIVE RISKS', 110, cursorY);
    cursorY += 8;

    doc.setFont('helvetica', 'normal');
    const gapsText = comparison.gaps.map((g, i) => `${i+1}. ${g}`).join('\n');
    const risksText = comparison.risks.map((r, i) => `${i+1}. ${r}`).join('\n');
    
    const gapsLines = doc.splitTextToSize(gapsText, 80);
    const risksLines = doc.splitTextToSize(risksText, 80);
    const multiLineHeight = Math.max(gapsLines.length, risksLines.length) * 4.5;
    
    checkPageOverflow(multiLineHeight + 10);
    doc.text(gapsLines, margin, cursorY);
    doc.text(risksLines, 110, cursorY);
    
    cursorY += multiLineHeight + 15;

    // Strategic Selection
    const reasonLines = doc.splitTextToSize(comparison.selectionReasoning, 160);
    const boxHeight = 25 + (reasonLines.length * 4);
    
    checkPageOverflow(boxHeight + 40);
    doc.setFillColor(secondaryColor);
    doc.rect(margin, cursorY, 170, boxHeight, 'F');
    doc.setTextColor(primaryColor);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('STRATEGIC SELECTION', margin + 5, cursorY + 8);
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.text(comparison.winningVenture.toUpperCase(), margin + 5, cursorY + 15);
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(reasonLines, margin + 5, cursorY + 22);
    cursorY += boxHeight + 10;

    const recLines = doc.splitTextToSize(comparison.recommendation, 160);
    const recBoxHeight = 15 + (recLines.length * 4);
    checkPageOverflow(recBoxHeight + 10);
    
    doc.setFillColor(248, 250, 252);
    doc.rect(margin, cursorY, 170, recBoxHeight, 'F');
    doc.setTextColor(secondaryColor);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('FINAL RECOMMENDATION', margin + 5, cursorY + 8);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(recLines, margin + 5, cursorY + 15);
    cursorY += recBoxHeight + 15;
  }

  // 1.5. Compared Ventures Overview (Thumbnails)
  checkPageOverflow(30);
  doc.setTextColor(primaryColor);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('COMPARED VENTURES OVERVIEW', margin, cursorY);
  cursorY += 10;

  ideas.forEach((idea) => {
    const isDiscovery = idea.hubOrigin === 'discovery';
    const cardHeight = 45;
    checkPageOverflow(cardHeight + 10);

    // Card boundary rectangle (no rounded corners)
    doc.setDrawColor(50, 45, 41, 35); // solid subtle grey line (represented out of 255)
    // Let's use RGB components
    doc.setDrawColor(200, 200, 200);
    doc.setFillColor(255, 255, 255);
    doc.rect(margin, cursorY, 170, cardHeight, 'FD');

    // Header bg inside card
    doc.setFillColor(248, 250, 252);
    doc.rect(margin + 0.5, cursorY + 0.5, 169.2, 9, 'F');

    // Title text
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(15, 23, 42); // slate-900
    doc.text(idea.title.toUpperCase(), margin + 5, cursorY + 6.5);

    // Category label
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(100, 116, 139); // slate-500
    const catText = isDiscovery ? 'DISTRICT INTEL RECON' : idea.category.toUpperCase();
    doc.text(catText, 185, cursorY + 6.5, { align: 'right' });

    // Description text
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105); // slate-600
    const descLines = doc.splitTextToSize(`"${idea.description}"`, 95);
    doc.text(descLines, margin + 5, cursorY + 16);

    // Separator line
    doc.setDrawColor(226, 232, 240); // slate-200
    doc.line(margin + 110, cursorY + 12, margin + 110, cursorY + cardHeight - 3);

    // Stats table columns
    const stats = [
      { label: 'DEMAND', val: isDiscovery ? 'N/A' : `${idea.marketDemandScore || 85}%` },
      { label: 'GAP INDEX', val: isDiscovery ? 'N/A' : `${idea.saturationIndex || 30}%` },
      { label: 'ROI', val: isDiscovery ? 'HIGH' : (idea.potentialROI || 'N/A') },
      { label: isDiscovery ? 'IDENTITY' : 'CAPITAL', val: isDiscovery ? (idea.densityInsight || 'VERIFIED') : (idea.estimatedStartupCost || 'N/A') }
    ];

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    stats.forEach((stat, sIdx) => {
      const statY = cursorY + 16 + (sIdx * 6.5);
      
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 116, 139); // slate-500
      doc.text(stat.label, margin + 114, statY);
      
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42); // slate-900
      doc.text(String(stat.val).toUpperCase(), 185, statY, { align: 'right' });
    });

    cursorY += cardHeight + 8;
  });

  cursorY += 5;

  // 2. Comparison Matrix
  checkPageOverflow(40);
  doc.setTextColor(primaryColor);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('COMPARISON MATRIX', margin, cursorY);
  cursorY += 10;

  const colWidth = 170 / (ideas.length + 1);
  const headers = ['METRIC', ...ideas.map(i => i.title.toUpperCase())];
  
  doc.setFillColor(241, 245, 249);
  doc.rect(margin, cursorY, 170, 12, 'F');
  doc.setTextColor(secondaryColor);
  doc.setFontSize(8);
  
  headers.forEach((h, i) => {
    const x = margin + (i * colWidth);
    doc.text(doc.splitTextToSize(h, colWidth - 5), x + 2, cursorY + 8);
  });
  
  cursorY += 12;

  const rows = [
    ['CATEGORY', ...ideas.map(i => i.category)],
    ['EST. COST', ...ideas.map(i => i.fullPath?.startupCost || i.estimatedStartupCost || 'N/A')],
    ['ROI', ...ideas.map(i => i.potentialROI || 'N/A')],
    ['RISK SCORE', ...ideas.map(i => i.riskGuard?.score?.toString() || 'N/A')],
    ['BREAK-EVEN', ...ideas.map(i => i.fullPath?.breakEvenTimeline || 'N/A')],
    ['MARKET GAP', ...ideas.map(i => i.competition?.ourAdvantage || 'N/A')]
  ];

  // Add AI-generated comparison matrix rows if available
  if (comparison && comparison.comparisonMatrix) {
    comparison.comparisonMatrix.forEach(row => {
      const aiRow = [row.feature.toUpperCase()];
      ideas.forEach(idea => {
        aiRow.push(row.values[idea.title] || row.values[Object.keys(row.values)[0]] || 'N/A');
      });
      rows.push(aiRow);
    });
  }

  rows.forEach((row, rowIndex) => {
    checkPageOverflow(18);
    if (rowIndex % 2 === 0) {
      doc.setFillColor(248, 250, 252);
      doc.rect(margin, cursorY, 170, 15, 'F');
    }
    
    doc.setTextColor(secondaryColor);
    row.forEach((cell, i) => {
      const x = margin + (i * colWidth);
      const text = cell || 'N/A';
      const lines = doc.splitTextToSize(text, colWidth - 5);
      doc.text(lines, x + 2, cursorY + 8);
    });
    cursorY += 15;
  });

  // --- Dynamic Post-processing Header & Footer ---
  const pageCount = (doc as any).internal.getNumberOfPages();
  const timestampStr = new Date().toLocaleString();
  const reportId = new Date().getTime().toString(36).toUpperCase();
  
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    
    if (i === 1) {
      // Cover page footer copyright info
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184); // slate-400
      doc.text('© eNvesta AI. All security intelligence cleared for dissemination.', 105, 275, { align: 'center' });
      continue;
    }

    const displayPage = i - 1;
    const displayTotal = pageCount - 1;
    
    // Draw / overlay the header band '#322D29'
    doc.setFillColor('#322D29');
    doc.rect(0, 0, 210, 25, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    
    // eNvesta AI with AI in Orange color not just eNvesta
    let currentX = margin;
    doc.text('ENVESTA ', currentX, 15);
    currentX += doc.getTextWidth('ENVESTA ');
    
    doc.setTextColor('#F97316'); // Orange
    doc.text('AI ', currentX, 15);
    currentX += doc.getTextWidth('AI ');
    
    doc.setTextColor(255, 255, 255);
    doc.text('INTEL COMPARISON REPORT', currentX, 15);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(timestampStr, 115, 15, { align: 'center' });
    doc.text(`PAGE ${displayPage} OF ${displayTotal}`, 190, 15, { align: 'right' });

    // Footer
    doc.setFillColor(241, 245, 249);
    doc.rect(0, 275, 210, 22, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.line(0, 275, 210, 275);
    
    // Tiny disclaimer text
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5.5);
    doc.setTextColor(100, 116, 139);
    const disclaimerText = "eNvesta AI provides market intelligence and business planning tools, not financial, investment, or legal advice. All outputs are algorithmic estimates based on aggregated data and your inputs. Consult qualified professionals before committing capital. Past performance of similar businesses does not guarantee future results.";
    const disclaimerLines = doc.splitTextToSize(disclaimerText, 170);
    doc.text(disclaimerLines, margin, 280);
    
    doc.setFontSize(7);
    doc.text(`Report ID: #${reportId}`, margin, 293);
    doc.text(`Page ${displayPage} of ${displayTotal}`, 190, 293, { align: 'right' });
    doc.setFont('helvetica', 'bold');
    
    if (branding?.name) {
      if (logoData) {
        try {
          doc.addImage(logoData, 'PNG', 105 - 15, 285, 8, 8);
          doc.text(branding.name, 105, 293, { align: 'center' });
        } catch (e) {
          doc.text(branding.name, 105, 293, { align: 'center' });
        }
      } else {
        doc.text(branding.name, 105, 293, { align: 'center' });
      }
    } else {
      doc.text('eNvesta AI Intelligence Engine v2.5', 105, 293, { align: 'center' });
    }
  }

  doc.save(`eNvesta_Comparison_Matrix_${new Date().getTime()}.pdf`);
};
